/**
 * v-permission Directive
 *
 * Vue directive for permission-based element visibility and interaction.
 *
 * Features:
 * - Disable elements without permission (default)
 * - Hide elements completely with hide: true
 * - Automatically add tooltip explaining why action is denied
 * - Re-evaluate permissions when they change
 *
 * Usage:
 * ```vue
 * <!-- Disable button if no permission -->
 * <Button
 *   v-permission="{ action: 'delete', resource: 'customers' }"
 *   label="Delete"
 * />
 *
 * <!-- Hide button completely if no permission -->
 * <Button
 *   v-permission="{ action: 'delete', resource: 'customers', hide: true }"
 *   label="Delete"
 * />
 * ```
 */

import type { Directive } from 'vue'
import {
  hasPermission,
  getPermissionMessage,
  type ActionType,
  type ResourceType,
  type UserRole,
} from '@/types/permissions'

interface PermissionBinding {
  action: ActionType
  resource: ResourceType
  hide?: boolean // If true, hide element entirely. If false, just disable it.
}

// WeakMap to store original display values without polluting DOM elements
const originalDisplayMap = new WeakMap<HTMLElement, string>()

const DISABLED_CLASS = 'permission-disabled'

/**
 * Apply permission-based restrictions to an element
 */
function applyPermission(
  el: HTMLElement,
  binding: PermissionBinding,
  userRole: UserRole | undefined,
) {
  const { action, resource, hide = false } = binding
  const hasAccess = hasPermission(userRole, action, resource)

  if (hasAccess) {
    // User has permission - ensure element is enabled/visible
    el.classList.remove(DISABLED_CLASS)

    // Remove disabled attribute if it was added by this directive
    if (el.hasAttribute('data-permission-disabled')) {
      el.removeAttribute('disabled')
      el.removeAttribute('data-permission-disabled')
    }

    // Restore original display if it was hidden
    const originalDisplay = originalDisplayMap.get(el)
    if (originalDisplay !== undefined) {
      el.style.display = originalDisplay
      originalDisplayMap.delete(el)
    }

    // Remove tooltip
    if (el.hasAttribute('data-permission-tooltip')) {
      el.removeAttribute('title')
      el.removeAttribute('data-permission-tooltip')
    }
  } else {
    // User lacks permission
    if (hide) {
      // Hide element completely
      if (!originalDisplayMap.has(el)) {
        originalDisplayMap.set(el, el.style.display)
      }
      el.style.display = 'none'
    } else {
      // Disable element
      el.classList.add(DISABLED_CLASS)

      // Add disabled attribute for interactive elements
      if (
        el.tagName === 'BUTTON' ||
        el.tagName === 'INPUT' ||
        el.tagName === 'SELECT' ||
        el.tagName === 'TEXTAREA'
      ) {
        el.setAttribute('disabled', 'disabled')
        el.setAttribute('data-permission-disabled', 'true')
      }

      // Add explanatory tooltip
      const message = getPermissionMessage(action, resource)
      el.setAttribute('title', message)
      el.setAttribute('data-permission-tooltip', 'true')

      // Add pointer-events none to prevent interaction
      el.style.pointerEvents = 'none'
      el.style.opacity = '0.5'
    }
  }
}

// Type for Pinia's internal state structure
interface PiniaState {
  state: {
    value: {
      auth?: {
        user?: {
          role?: UserRole
        }
      }
    }
  }
}

// Type for localStorage user data
interface StoredUserData {
  user?: {
    role?: UserRole
  }
}

/**
 * Get current user role from auth store
 * We need to access the store without using composables since this is a directive
 */
function getUserRole(): UserRole | undefined {
  // Access the auth store from window/global if available
  // This works because Pinia stores are singletons
  try {
    const pinia = (window as unknown as { __PINIA__?: PiniaState }).__PINIA__
    if (pinia?.state?.value?.auth?.user?.role) {
      return pinia.state.value.auth.user.role
    }

    // Fallback: try to get from localStorage
    const authData = localStorage.getItem('hudstore_user_data')
    if (authData) {
      const parsed = JSON.parse(authData) as StoredUserData
      return parsed.user?.role
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('v-permission: Unable to determine user role', error)
    }
  }

  return undefined
}

export const vPermission: Directive<HTMLElement, PermissionBinding> = {
  mounted(el, binding) {
    const userRole = getUserRole()
    applyPermission(el, binding.value, userRole)
  },

  updated(el, binding) {
    const userRole = getUserRole()
    applyPermission(el, binding.value, userRole)
  },

  unmounted(el) {
    // Cleanup
    el.classList.remove(DISABLED_CLASS)
    el.removeAttribute('data-permission-disabled')
    el.removeAttribute('data-permission-tooltip')
    el.style.pointerEvents = ''
    el.style.opacity = ''

    const originalDisplay = originalDisplayMap.get(el)
    if (originalDisplay !== undefined) {
      el.style.display = originalDisplay
      originalDisplayMap.delete(el)
    }
  },
}

// Export default for easier registration
export default vPermission
